<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <!-- Gate: host/cohost access -->
      <div class="col-md-8" v-if="eventAcess === 'yes'">
        <!-- Top bar -->
        <div class="page-bar py-3">
          <div class="container">
            <div class="d-flex align-items-center">
              <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div class="page-title flex-grow-1">{{ eventTitle || 'Event' }}</div>

              <div class="dropdown">
                <button class="dot-btn btn p-1 action-menu-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <div class="dropdown-menu dropdown-menu-right action-menu">
                  <router-link
                    class="dropdown-item action-menu__item"
                    v-if="eventType == 'new' && cohostAddStatus == '1'"
                    :to="`/cohost/${eventId}`"
                  >
                    <i class="far fa-edit mr-2"></i><span>Add CoHost</span>
                  </router-link>

                  <router-link class="dropdown-item action-menu__item" v-if="eventEdit == 'yes'" :to="`/edit-invitation/${eventId}`">
                    <i class="far fa-copy mr-2"></i><span>Edit Invitation</span>
                  </router-link>

                  <router-link class="dropdown-item action-menu__item" v-if="eventEdit == 'yes'" :to="`/event-settings/${eventId}`">
                    <i class="fas fa-sliders-h mr-2"></i><span>Edit Event Settings</span>
                  </router-link>

                  <!-- Share via & QR -->
                  <a class="dropdown-item action-menu__item" href="#" @click.prevent="showShareVia = true">
                    <i class="far fa-share-square mr-2"></i><span>Share Via</span>
                  </a>
                  <a class="dropdown-item action-menu__item" href="#" @click.prevent="showQrModal = true">
                    <i class="fas fa-qrcode mr-2"></i><span>Album QR Code</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main content -->
        <div class="container mt-3 mb-5 shadow-sm bg-white py-2 rounded" v-if="loaded">
          <!-- Carousel -->
          <div class="hero my-3" v-if="mediaSlides.length">
            <div class="slide-frame" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
              <div
                v-if="useContain && currentSlideBgSrc && currentSlideMedia && currentSlideMedia.type === 'image'"
                class="slide-bg-blur"
                :style="{ backgroundImage: `url(${currentSlideBgSrc})` }"
              ></div>

              <img
                v-if="currentSlideMedia && currentSlideMedia.type === 'image'"
                :key="`slide-img-${currentSlide}`"
                :src="currentSlideMedia.src"
                class="slide-img"
                :class="useContain ? 'fit-contain' : 'fit-cover'"
                :alt="`Slide ${currentSlide + 1}`"
                @load="onImgLoad"
              />
              <template v-else-if="currentSlideMedia && currentSlideMedia.type === 'video'">
                <div
                  v-if="!isCurrentVideoPlaying"
                  class="video-preview-shell"
                  @click="startVideoPlayback"
                >
                  <img
                    :src="currentSlideMedia.thumb || VIDEO_THUMB_PLACEHOLDER"
                    class="slide-img fit-cover"
                    :alt="`Video preview ${currentSlide + 1}`"
                  />
                  <button type="button" class="video-preview-play" aria-label="Play video">
                    <i class="fas fa-play"></i>
                  </button>
                </div>
                <template v-if="isCurrentVideoPlaying">
                  <vue-plyr
                    v-if="currentSlideMedia.videoKind === 'url'"
                    :key="`slide-player-file-${currentSlide}-${currentSlideMedia.src}`"
                    class="slide-player"
                    :options="plyrOptions"
                  >
                    <video
                      controls
                      playsinline
                      preload="metadata"
                      disablePictureInPicture
                      :poster="currentSlideMedia.thumb || ''"
                    >
                      <source :src="currentSlideMedia.src" />
                    </video>
                  </vue-plyr>
                  <vue-plyr
                    v-else-if="currentSlideMedia.videoKind === 'youtube'"
                    :key="`slide-player-yt-${currentSlide}-${currentSlideMedia.youtubeId}`"
                    class="slide-player"
                    :options="plyrOptions"
                  >
                    <div class="plyr__video-embed" :data-poster="currentSlideMedia.thumb || ''">
                      <iframe
                        :src="`https://www.youtube.com/embed/${currentSlideMedia.youtubeId}?playsinline=1&modestbranding=1&rel=0&enablejsapi=1`"
                        allowfullscreen
                        allowtransparency
                        allow="autoplay"
                      ></iframe>
                    </div>
                  </vue-plyr>
                  <div
                    v-else
                    :key="`slide-player-iframe-${currentSlide}-${currentSlideMedia.embedUrl}`"
                    class="slide-iframe-wrap"
                  >
                    <iframe
                      class="slide-iframe"
                      :src="currentSlideMedia.embedUrl || currentSlideMedia.src"
                      title="Video player"
                      loading="lazy"
                      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                      allowfullscreen
                    ></iframe>
                  </div>
                </template>
              </template>

              <button v-if="mediaSlides.length > 1" class="nav prev" @click="prevSlide" aria-label="Previous">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button v-if="mediaSlides.length > 1" class="nav next" @click="nextSlide" aria-label="Next">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

            <div class="thumbs mt-2" v-if="mediaSlides.length > 1">
              <button
                v-for="(item, i) in mediaSlides"
                :key="`thumb-${i}-${item.type}-${item.src}`"
                type="button"
                class="thumb-btn"
                :class="{ active: i === currentSlide }"
                @mousedown.prevent
                @click.prevent="goToSlide(i)"
                :title="`Go to slide ${i+1}`"
              >
                <img v-no-right-click :src="item.thumb || item.src" class="thumb" alt="thumbnail" />
                <span v-if="item.type === 'video'" class="thumb-play" aria-hidden="true">
                  <i class="fas fa-play"></i>
                </span>
              </button>
            </div>
          </div> 
          
          <!-- Title + Host -->
          <h5 class="mb-1">{{ eventTitle || 'Event' }}</h5>
          <div class="mb-3 text-small">
            Host: <strong>{{ hostName || '-' }}</strong>
          </div>

          <!-- Event details -->
          <div class="mb-3">
            <div class="section-title">Event Details</div>

            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📅</span>
              <div>{{ eventDateRange }}</div>
            </div>

            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📍</span>
              <div class="flex-grow-1 text-dark">
                <template v-if="eventPlace">{{ eventPlace }}</template>
                <template v-if="doorNo">, {{ doorNo }}</template>
                <template v-if="Address">, {{ Address }}</template>
              </div>
              <a
                v-if="lat && lng"
                :href="mapHref"
                target="_blank"
                rel="noopener"
                class="ml-2 link-arrow"
                title="Open in Maps"
              >
                <i class="fas fa-location-arrow"></i>
              </a>
            </div>

            <div v-if="hasMap" class="map-card mt-3">
              <div class="map-card__header">
                <span><i class="fas fa-map-marked-alt mr-2"></i>Event Location</span>
                <a :href="mapHref" target="_blank" rel="noopener">Open in Google Maps</a>
              </div>
              <div class="map-frame">
                <iframe
                  class="map-frame__iframe"
                  :src="mapEmbedSrc"
                  loading="lazy"
                  allowfullscreen
                  referrerpolicy="no-referrer-when-downgrade"
                  aria-label="Event location map"
                ></iframe>
              </div>
            </div>
          </div>

          <!-- About -->
          <div class="mb-3" v-if="description">
            <div class="section-title">About The Event</div>
            <p class="mb-2">{{ description }}</p>
          </div>

          <!-- Invitation Count -->
          <div class="mb-3">
            <div class="section-title">Invitation Count</div>
            <div class="count-bar">
              <div class="count-pill count-total">
                <div class="num">{{ totals.total }}</div>
                <div class="lbl">Total</div>
              </div>
              <div class="count-pill count-sent">
                <div class="num">{{ totals.sent }}</div>
                <div class="lbl">Sent</div>
              </div>
              <div class="count-pill count-pending">
                <div class="num">{{ totals.pending }}</div>
                <div class="lbl">Pending</div>
              </div>
            </div>
          </div>

          <!-- RSVP Status -->
          <div class="mb-3" v-if="rsvpOption == 1">
            <div class="section-title">RSVP Status</div>
            <div class="rsvp-bar">
              <div class="rsvp-seg rsvp-yes"><span class="num">{{ rsvp.yes }}</span>Yes</div>
              <div class="rsvp-seg rsvp-maybe"><span class="num">{{ rsvp.maybe }}</span>Maybe</div>
              <div class="rsvp-seg rsvp-no"><span class="num">{{ rsvp.no }}</span>No</div>
              <div class="rsvp-seg rsvp-nr"><span class="num">{{ rsvp.noresponse }}</span>No Response</div>
            </div>
          </div>

          <!-- Attendance -->
          <div class="mb-4" v-if="rsvpOption == 1 && rsvpFamily == 1">
            <div class="section-title">How many are coming?</div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="att-card">
                  <div class="att-head confirmed d-flex align-items-center">Confirmed</div>
                  <div class="att-body">
                    <div class="row">
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.yesAdults }}</div>
                          <div class="small">Adults</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.yesChildren }}</div>
                          <div class="small">Children</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <div class="att-card">
                  <div class="att-head maybe d-flex align-items-center">Maybe</div>
                  <div class="att-body">
                    <div class="row">
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.maybeAdults }}</div>
                          <div class="small">Adults</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.maybeChildren }}</div>
                          <div class="small">Children</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr />

          <div class="mb-4">
            <div class="detail-action-card">
              <div class="detail-action-card__copy">
                <div class="detail-action-card__title">Albums</div>
                <p class="detail-action-card__text mb-0">
                  Enable album to share event photos with all guests or restrict access using face recognition, allowing guests to view only the photos they appear in.
                </p>
              </div>
              <router-link :to="`/album/${eventId}?source=my`" class="detail-action-card__button">
                View Album
              </router-link>
            </div>
          </div>

          <hr />

          <!-- Vendors -->
          <div class="mb-4">
            <div class="section-title">Vendors</div>
            <p class="text-small text-muted mb-2">
              Find and connect with top vendors for your event. Send enquiries to discover the best services that suit your needs.
            </p>
            <div class="d-flex">
              <router-link :to="`/detail/enquiry-list/${eventId}`" class="vendor-pill">
                Enquiries ({{ eventSendEnquiryCount }})
              </router-link>
              <router-link :to="`/detail/vendors/${eventId}`" class="vendor-pill">
                Hired Vendors
              </router-link>
            </div>
          </div>

          <hr />

          <div class="mb-4">
            <router-link :to="`/detail/posts/${eventId}`" class="detail-inline-link">
              <span class="detail-inline-link__copy">
                <span class="detail-inline-link__title">Post to this event</span>
                <span class="detail-inline-link__text">Post updates, announcements, or messages for this event</span>
              </span>
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </router-link>
          </div>

          <hr />
 
          <!-- Guests --> 
          <div class="mb-3">
            <div class="badge-tabs" role="tablist" aria-label="Guest tabs">
              <button
                v-for="tab in guestTabs"
                :key="tab.key"
                type="button"
                class="badge-tab"
                :class="{ active: tab.key === activeGuestsTab }"
                @click="setActiveGuestsTab(tab.key)"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>

          <GuestsNotesList
            v-if="isGuestsNotesTab"
            :notes="guestNotes"
            :loading="guestNotesLoading"
          />
          
          <GuestsList
            v-else
            :members="members"
            :event-id="eventId"
            :session-user-id="userId"
            :event-type="eventType"
            :rsvp-option="rsvpOption"
            :rsvp-family="rsvpFamily"
            @changed="loadInvitation"
          />
        </div>

        <!-- Loading -->
        <div v-else class="text-center p-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2 text-muted small">Fetching invitation details…</div>
        </div>
      </div>

      <!-- No access -->
      <div class="col-md-8" v-else-if="eventAcess === 'no'">
        <div class="card">
          <div class="card-body text-center py-4">
            <i class="fas fa-user fa-3x mb-2"></i>
            <p>You are not host for viewing this invitation</p>
          </div>
        </div>
      </div>

      <!-- Pending access -->
      <div class="col-md-8" v-else>
        <div class="card skeleton-card">
          <div class="card-body">
            <div class="skeleton-line skeleton-line--lg mb-3"></div>
            <div class="skeleton-line skeleton-line--md mb-3"></div>
            <div class="skeleton-line skeleton-line--sm mb-4"></div>
            <div class="skeleton-block mb-3"></div>
            <div class="skeleton-line skeleton-line--sm mb-2"></div>
            <div class="skeleton-line skeleton-line--md"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Share Via Modal -->
    <ShareViaModal
      :visible="showShareVia"
      :title="eventTitle"
      :image="shareImage"
      :description="description"
      :dates="shareDates"
      :host="hostName"
      :location="eventPlace"
      :address="shareAddress"
      :map-url="mapHref"
      :url="shareUrl"
      @close="showShareVia = false"
    />

    <!-- QR Share Modal -->
    <QrShareModal
      :visible="showQrModal"
      :qrSrc="qrcodeImage"
      :url="webqrimage"
      @close="showQrModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import ShareViaModal from '@/components/common/ShareViaModal.vue'
import QrShareModal from '@/components/common/QrShareModal.vue'
import GuestsList from '@/components/guests/GuestsList.vue'
import GuestsNotesList from '@/components/guests/GuestsNotesList.vue'
import { formatEventDateRange } from '@/utils/date'
import BackButton from '@/components/common/BackButton.vue'

export default {
  name: 'InvitationDetail',
  components: { ShareViaModal, QrShareModal, GuestsList, GuestsNotesList, BackButton },
  setup () {
    const route = useRoute()
    const session = useSessionStore()
    const userId = computed(() => session?.userId || '')

    // --- Event ID derivation ---
    const deriveEventId = (r) => {
      const cand = [
        r.params?.eventid, r.params?.id, r.params?.eventId,
        r.query?.eventid, r.query?.id, r.query?.eventId
      ].find(v => v != null && String(v).trim() !== '')
      if (cand) return String(cand)
      const lastSeg = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
      return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null
    }

    const eventId = ref(deriveEventId(route))
    const resetScrollTop = () => {
      if (typeof window === 'undefined') return
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
    const plyrOptions = {
      controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'fullscreen'],
      settings: ['speed'],
    }
    const loaded = ref(false)
    const raw = ref(null)
    const members = ref([])
    const guestNotes = ref([])
    const guestNotesLoading = ref(false)
    const guestNotesLoaded = ref(false)
    const activeGuestsTab = ref('guests')

    // Share modals
    const showShareVia = ref(false)
    const showQrModal = ref(false)

    // Carousel
    const currentSlide = ref(0)
    const autoTimer = ref(null)
    const AUTOPLAY_MS = 4000
    const naturalW = ref(0)
    const naturalH = ref(0)
    const RATIO_169 = 16 / 9
    function onImgLoad (e) {
      const img = e.target
      naturalW.value = img.naturalWidth || img.width || 0
      naturalH.value = img.naturalHeight || img.height || 0
    }
    const useContain = computed(() => {
      const w = naturalW.value; const h = naturalH.value
      if (!w || !h) return true
      return (w / h) < RATIO_169
    })
    const nextSlide = () => {
      const n = mediaSlides.value.length
      if (!n) return
      currentSlide.value = (currentSlide.value + 1) % n
    }
    const prevSlide = () => {
      const n = mediaSlides.value.length
      if (!n) return
      currentSlide.value = (currentSlide.value - 1 + n) % n
    }
    const goToSlide = (i) => {
      const n = mediaSlides.value.length
      if (!n) return
      currentSlide.value = Math.max(0, Math.min(i, n - 1))
    }
    const startAuto = () => {
      stopAuto()
      if (mediaSlides.value.length > 1) autoTimer.value = setInterval(nextSlide, AUTOPLAY_MS)
    }
    const stopAuto = () => {
      if (autoTimer.value) { clearInterval(autoTimer.value); autoTimer.value = null }
    }
    const pauseAuto = stopAuto
    const resumeAuto = () => { if (mediaSlides.value.length > 1) startAuto() }

    // Access/edit flags
    const cohostState = ref(null)
    const eventAccessState = ref(null)
    const eventEditState = ref(null)
    const eventDeleteState = ref(null)

    const cohostAddStatus = computed(() => (cohostState.value?.cohost_add_status ?? '0').toString())
    const eventAcess = computed(() => {
      const access = eventAccessState.value?.event_access
      if (access === undefined || access === null || String(access).trim() === '') return 'pending'
      return String(access).toLowerCase()
    })
    const eventEdit = computed(() => (eventEditState.value?.edit_option ?? 'no').toString())
    const eventDelete = computed(() => (eventDeleteState.value?.delete_option ?? 'no').toString())

    // Fetch
    async function loadInvitation () {
      try {
        loaded.value = false
        eventAccessState.value = { event_access: null }
        guestNotes.value = []
        guestNotesLoaded.value = false
        guestNotesLoading.value = false
        activeGuestsTab.value = 'guests'
        if (!eventId.value) { raw.value = null; members.value = []; return }

        const fd = new FormData()
        fd.append('userid', userId.value)
        fd.append('eventid', eventId.value)

        const res = await api.getInvitationDetail(fd)
        const payload = res?.data ?? res
        const ok = payload?.status === 'ok' || payload?.status === true
        if (!ok) { raw.value = null; members.value = []; return }

        raw.value = payload

        const guestsArray = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : []
        members.value = guestsArray

        cohostState.value = { cohost_add_status: payload?.cohost_add_status ?? payload?.data?.cohost_add_status ?? '0' }
        eventAccessState.value = { event_access: payload?.event_access ?? payload?.data?.event_access ?? 'no' }
        eventEditState.value = { edit_option: payload?.edit_option ?? payload?.data?.edit_option ?? 'no' }
        eventDeleteState.value = { delete_option: payload?.delete_option ?? payload?.data?.delete_option ?? 'no' }

        setDefaultGuestsTab()
        if (isNotesTabKey(activeGuestsTab.value)) ensureGuestNotesLoaded()

        // reset slider
        currentSlide.value = 0
        stopAuto()
        naturalW.value = 0
        naturalH.value = 0
        if (mediaSlides.value.length > 1) startAuto()
      } catch (e) {
        console.error('Error fetching invitation detail:', e)
        raw.value = null
        members.value = []
      } finally {
        loaded.value = true
      }
    }

    const labelsGuestsRaw = computed(() => {
      const src = raw.value?.labels_guests ?? raw.value?.data?.labels_guests ?? []
      if (Array.isArray(src)) return src
      if (typeof src === 'string') return src.split(',').map(s => s.trim()).filter(Boolean)
      return []
    })

    const guestTabs = computed(() => labelsGuestsRaw.value.map((item, idx) => {
      const label = typeof item === 'string'
        ? item
        : item?.label ?? item?.name ?? item?.title ?? item?.text ?? ''
      const cleanedLabel = (label || '').trim() || `Tab ${idx + 1}`
      const keySource = item?.type ?? item?.key ?? item?.id ?? cleanedLabel
      const normalizedKey = String(keySource || `tab-${idx}`).trim().toLowerCase().replace(/\s+/g, '_') || `tab-${idx}`
      return { key: normalizedKey, label: cleanedLabel }
    }))

    const isNotesTabKey = (key) => {
      const k = String(key || '').toLowerCase()
      return k === 'notes' || k.includes('note')
    }

    const setDefaultGuestsTab = () => {
      const fallback = guestTabs.value[0]?.key || 'guests'
      activeGuestsTab.value = fallback
    }

    watch(guestTabs, (tabs) => {
      const fallback = tabs[0]?.key || 'guests'
      if (!tabs.find(t => t.key === activeGuestsTab.value)) activeGuestsTab.value = fallback
    })

    const setActiveGuestsTab = (key) => {
      activeGuestsTab.value = key
      if (isNotesTabKey(key)) ensureGuestNotesLoaded()
    }

    const ensureGuestNotesLoaded = async () => {
      if (!eventId.value || guestNotesLoading.value || guestNotesLoaded.value) return
      await loadGuestNotes()
    }

    async function loadGuestNotes () {
      if (!eventId.value) { guestNotes.value = []; guestNotesLoaded.value = false; return }
      try {
        guestNotesLoading.value = true
        const fd = new FormData()
        fd.append('event_id', eventId.value)
        const res = await api.getNotesMembersEventDetail(fd)
        const payload = res?.data ?? res
        const ok = payload?.status === 'ok' || payload?.status === true
        guestNotes.value = ok && Array.isArray(payload?.data) ? payload.data : []
        guestNotesLoaded.value = true
      } catch (e) {
        console.error('Error fetching guest notes:', e)
        guestNotes.value = []
        guestNotesLoaded.value = false
      } finally {
        guestNotesLoading.value = false
      }
    }

    watch(activeGuestsTab, (tab) => {
      if (isNotesTabKey(tab)) ensureGuestNotesLoaded()
    })

    const isGuestsNotesTab = computed(() => isNotesTabKey(activeGuestsTab.value))

    // Derived info
    const info = computed(() => raw.value?.eventsinfo?.[0] || {})
    const eventTitle = computed(() => info.value.eventname || info.value.orgname || '')
    const eventType = computed(() => info.value.event_type || 'new')
    const hostName = computed(() => info.value.hostname || raw.value?.host_name || '')
    const qrcodeImage = computed(() => info.value.qrcode_image || raw.value?.qrcode_image || '')
    const shareUrl = computed(() => info.value.share_url || '')

    const parseYouTubeId = (url) => {
      if (!url) return ''
      const s = String(url).trim()
      const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i)
      return m?.[1] || ''
    }
    const isDirectVideoUrl = (url) => {
      const s = String(url || '').trim().toLowerCase().split('?')[0].split('#')[0]
      return /\.(mp4|mov|webm|m3u8|ogg|ogv)$/i.test(s)
    }
    const isCanvaWatchUrl = (url) => {
      const s = String(url || '').trim().toLowerCase()
      return s.includes('canva.com') && s.includes('/watch')
    }
    const withAutoplayParam = (url) => {
      if (!url) return ''
      try {
        const u = new URL(url)
        u.searchParams.set('autoplay', '1')
        return u.toString()
      } catch (_) {
        return url
      }
    }
    const toCanvaEmbedUrl = (url) => {
      const s = String(url || '').trim()
      if (!s) return ''
      try {
        const u = new URL(s)
        if (u.hostname.includes('canva.com') && u.pathname.includes('/watch')) {
          u.pathname = u.pathname.replace('/watch', '/view')
          u.searchParams.set('embed', '1')
          u.searchParams.set('autoplay', '1')
          return u.toString()
        }
      } catch (_) {
        return s
      }
      return s
    }
    const VIDEO_THUMB_PLACEHOLDER = 'https://dummyimage.com/320x180/222/fff.png&text=Video'
    const mediaSlides = computed(() => {
      const slides = []
      const photosNew = Array.isArray(info.value.photos_new) ? info.value.photos_new : []
      const photoNewVideoPreview = photosNew
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return ''
          const type = String(entry.type || '').trim().toLowerCase()
          const value = String(entry.value || '').trim()
          return type === 'video' ? value : ''
        })
        .find(Boolean) || ''

      const vLink = String(info.value.video_link || raw.value?.video_link || '').trim()
      const baseVideoThumb = String(
        photoNewVideoPreview ||
        info.value.video_thumbnail ||
        info.value.video_thumb ||
        raw.value?.video_thumbnail ||
        raw.value?.video_thumb ||
        ''
      ).trim()

      const pushImage = (v) => {
        const src = String(v || '').trim()
        if (!src) return
        slides.push({ type: 'image', src, thumb: src })
      }
      const pushVideo = (thumbCandidate = '') => {
        if (!vLink) return false
        const yid = parseYouTubeId(vLink)
        const canvaEmbedUrl = isCanvaWatchUrl(vLink) ? toCanvaEmbedUrl(vLink) : ''
        const isFileUrl = isDirectVideoUrl(vLink)
        const isIframeSource = !yid && !isFileUrl
        const thumb = String(thumbCandidate || baseVideoThumb || VIDEO_THUMB_PLACEHOLDER).trim()
        slides.push({
          type: 'video',
          src: vLink,
          thumb,
          videoKind: yid ? 'youtube' : (isIframeSource ? 'iframe' : 'url'),
          youtubeId: yid || '',
          embedUrl: yid
            ? `https://www.youtube.com/embed/${yid}`
            : (canvaEmbedUrl ? canvaEmbedUrl : withAutoplayParam(vLink))
        })
        return true
      }

      if (photosNew.length) {
        let hasVideoEntry = false
        info.value.photos_new.forEach((entry) => {
          if (entry && typeof entry === 'object') {
            const type = String(entry.type || '').trim().toLowerCase()
            const value = String(entry.value || '').trim()
            if (type === 'video') {
              hasVideoEntry = true
              if (!pushVideo(value) && value) pushImage(value)
              return
            }
            if (value) pushImage(value)
            return
          }
          pushImage(entry)
        })
        if (!hasVideoEntry && vLink) pushVideo(baseVideoThumb)
      } else if (Array.isArray(info.value.photos) && info.value.photos.length) {
        info.value.photos.forEach(pushImage)
        if (vLink) pushVideo(baseVideoThumb)
      }
      if (!slides.length) {
        pushImage(info.value.type_image)
        if (vLink) pushVideo(baseVideoThumb)
        else if (baseVideoThumb) pushImage(baseVideoThumb)
      }

      const out = []
      const seen = new Set()
      for (const item of slides) {
        const key = `${item.type}:${item.src}`
        if (!item.src || seen.has(key)) continue
        seen.add(key)
        out.push(item)
      }
      return out
    })
    const currentSlideMedia = computed(() => mediaSlides.value[currentSlide.value] || null)
    const currentSlideBgSrc = computed(() => currentSlideMedia.value?.thumb || currentSlideMedia.value?.src || '')
    const videoPlaybackSlide = ref(null)
    const isCurrentVideoPlaying = computed(() => videoPlaybackSlide.value === currentSlide.value)
    const startVideoPlayback = () => {
      videoPlaybackSlide.value = currentSlide.value
      stopAuto()
    }
    watch(mediaSlides, (arr) => {
      if (!arr.length) { currentSlide.value = 0; stopAuto(); return }
      if (currentSlide.value >= arr.length) currentSlide.value = 0
      if (arr.length > 1 && !autoTimer.value) startAuto()
    })
    watch(currentSlide, () => { videoPlaybackSlide.value = null })
    watch(currentSlideMedia, (media) => {
      if (!media) return
      if (media.type === 'video' || isCurrentVideoPlaying.value) {
        stopAuto()
        return
      }
      if (mediaSlides.value.length > 1 && !autoTimer.value) startAuto()
    })

    const fromDate = computed(() => info.value.fromdate || '')
    const toDate = computed(() => info.value.todate || '')
    const fromTime = computed(() => (info.value.fromtime || '').trim())
    const toTime = computed(() => (info.value.totime || '').trim())
    const eventPlace = computed(() => info.value.event_place || '')
    const Address = computed(() => info.value.address || '')
    const doorNo = computed(() => info.value.door_no || '')
    const lat = computed(() => info.value.lat || '')
    const lng = computed(() => info.value.lng || '')
    const description = computed(() => raw.value?.event_description || info.value.description || '')
    const rsvpOption = computed(() => Number(raw.value?.rsvp_option ?? info.value.rsvp_option ?? 0) === 1 ? 1 : 0)
    const rsvpFamily = computed(() => Number(raw.value?.rsvp_family ?? info.value.rsvp_family ?? 0) === 1 ? 1 : 0)
    const webqrimage = computed(() => info.value.webqrimage || '')
    const shareAddress = computed(() => {
      const parts = [eventPlace.value, doorNo.value, Address.value].filter(Boolean)
      return parts.join(', ')
    })

    const totals = computed(() => ({
      total: Number(raw.value?.total_members_count || 0),
      sent: Number(raw.value?.sent_members_count || 0),
      pending: Number(raw.value?.notsent_members_count || 0)
    }))

    const rsvp = computed(() => ({
      yes: Number(raw.value?.yescount || 0),
      no: Number(raw.value?.nocount || 0),
      maybe: Number(raw.value?.maybecount || 0),
      noresponse: Number(raw.value?.noresponsecount || 0)
    }))

    const attendance = computed(() => ({
      yesAdults: Number(raw.value?.yesadult || 0),
      yesChildren: Number(raw.value?.yeschild || 0),
      maybeAdults: Number(raw.value?.mayadult || 0),
      maybeChildren: Number(raw.value?.maychild || 0)
    }))

    const eventSendEnquiryCount = computed(() => Number(raw.value?.eventSendEnquiryCount || 0))

    const mapHref = computed(() => {
      if (!lat.value || !lng.value) return '#'
      return `https://www.google.com/maps?q=${encodeURIComponent(lat.value)},${encodeURIComponent(lng.value)}`
    })
    const hasMap = computed(() => Boolean(lat.value && lng.value))
    const mapEmbedSrc = computed(() => {
      if (!hasMap.value) return ''
      const query = `${encodeURIComponent(lat.value)},${encodeURIComponent(lng.value)}`
      return `https://www.google.com/maps?q=${query}&hl=en&output=embed`
    })

    const shareDates = computed(() => formatEventDateRange({
      fromDate: fromDate.value,
      toDate: toDate.value,
      fromTime: fromTime.value,
      toTime: toTime.value
    }))
    const eventDateRange = computed(() => shareDates.value || '')
    const shareImage = computed(() => mediaSlides.value[0]?.thumb || mediaSlides.value[0]?.src || '')

    // Watch route changes (navigate between invitations)
    watch(() => route.fullPath, async () => {
      const next = deriveEventId(route)
      if (next !== eventId.value) {
        eventId.value = next
        await loadInvitation()
        await nextTick()
        resetScrollTop()
      }
    })

    onMounted(async () => {
      await loadInvitation()
      await nextTick()
      resetScrollTop()
      if (mediaSlides.value.length > 1) startAuto()
    })
    onBeforeUnmount(() => { stopAuto() })

    return {
      // route-derived
      eventId,
      userId,

      // state
      loaded,
      members,
      guestTabs,
      activeGuestsTab,
      setActiveGuestsTab,
      guestNotes,
      guestNotesLoading,
      isGuestsNotesTab,
      loadInvitation,

      // info
      eventTitle, eventType, qrcodeImage, hostName,
      fromDate, toDate, fromTime, toTime,
      eventPlace, Address, doorNo, lat, lng, mapHref,
      description, webqrimage, totals, rsvp, attendance, eventSendEnquiryCount, hasMap, mapEmbedSrc, rsvpOption, rsvpFamily,
      shareAddress,

      // formatting
      // carousel
      mediaSlides, currentSlideMedia, currentSlideBgSrc, currentSlide, isCurrentVideoPlaying, startVideoPlayback, nextSlide, prevSlide, goToSlide, pauseAuto, resumeAuto, onImgLoad, useContain,
      plyrOptions,

      // flags
      cohostAddStatus, eventAcess, eventEdit, eventDelete,

      // share modals
      showShareVia, showQrModal, shareDates, shareImage, shareUrl, eventDateRange,
      VIDEO_THUMB_PLACEHOLDER
    }
  }
}
</script>

<style>
/* Minimal modal css & helpers (no custom modals here, kept base styles for consistency) */
.gap-2 { gap: .5rem; }

:root{
  --brand-blue:#1da1f2;
  --brand-amber:#f5c542;
  --brand-purple:#6f42c1;
  --brand-green:#28a745;
  --brand-orange:#f39c12;
  --brand-red:#dc3545;
  --brand-primary:#0069d9;
}

/* Header */
.page-bar{ position: sticky; top: 0; z-index: 1000; background: #fff; border-bottom: 1px solid rgba(0,0,0,.06); }
.page-title{ font-weight: 600; font-size: 1.05rem; }

/* HERO / SLIDER */
.slide-frame{
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: .5rem;
  background: #000;
}
.slide-bg-blur{ position: absolute; inset: 0; background-size: cover; background-position: center; filter: blur(20px) brightness(0.9); transform: scale(1.1); }
.slide-img{ position: absolute; inset: 0; width: 100%; height: 100%; border-radius: .5rem; box-shadow: 0 2px 10px rgba(0,0,0,.08); }
.slide-img.fit-contain{ object-fit: contain; background: transparent; }
.slide-img.fit-cover{ object-fit: cover; object-position: top center; }
.video-preview-shell{
  position:absolute;
  inset:0;
  cursor:pointer;
}
.video-preview-play{
  position:absolute;
  left:50%;
  top:50%;
  transform:translate(-50%, -50%);
  width:64px;
  height:64px;
  border:0;
  border-radius:50%;
  background:rgba(14,165,233,.9);
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:22px;
  box-shadow:0 10px 24px rgba(2,6,23,.35);
}
.slide-player{
  position:absolute;
  inset:0;
  border-radius:.5rem;
  overflow:hidden;
  box-shadow:0 2px 10px rgba(0,0,0,.08);
  background:#000;
}
:deep(.slide-player .plyr){
  width:100%;
  height:100% !important;
  max-width:none;
  border-radius:.5rem;
  min-height:0;
}
:deep(.slide-player .plyr__video-wrapper){
  position:absolute;
  inset:0;
  height:100% !important;
  min-height:0;
  padding-bottom:0 !important;
}
:deep(.slide-player .plyr video){
  width:100%;
  height:100%;
  object-fit:cover;
  background:#000;
}
:deep(.slide-player .plyr__poster){
  background-size:cover !important;
  background-position:center !important;
}
:deep(.slide-player .plyr__video-embed){
  position:absolute;
  inset:0;
  height:100%;
}
:deep(.slide-player .plyr__video-embed iframe){
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
}
.slide-iframe-wrap{
  position:absolute;
  inset:0;
  border-radius:.5rem;
  overflow:hidden;
  background:#000;
}
.slide-iframe{
  width:100%;
  height:100%;
  border:0;
  display:block;
}

.nav{
  position: absolute; top: 50%; transform: translateY(-50%);
  border: 0; background: rgba(0,0,0,.35); color: #fff;
  width: 36px; height: 36px; border-radius: 50%;
  display:flex;align-items:center;justify-content:center; cursor: pointer;
}
.nav.prev{ left: 10px; } .nav.next{ right: 10px; }
.nav i{ font-size: 14px; }

.thumbs{ display: flex; flex-wrap: nowrap; overflow-x: auto; gap: .5rem; padding-bottom: .25rem; }
.thumb-btn{ padding: 0; border: 2px solid transparent; background: transparent; border-radius: .35rem; line-height: 0; flex: 0 0 auto; }
.thumb-btn.active{ border-color: var(--brand-primary); }
.thumb-btn{ position:relative; }
.thumb{ width: 88px; height: 56px; object-fit: cover; border-radius: .35rem; display: block; box-shadow: 0 1px 6px rgba(0,0,0,.08); }
.thumb-play{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  pointer-events:none;
}
.thumb-play i{
  width:20px;
  height:20px;
  border-radius:50%;
  background:rgba(255, 0, 0, 0.92);
  color:#ffffff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:9px;
}

/* Section titles */
.section-title{ font-weight: 600; font-size: 1rem; margin-bottom: .5rem; }

/* Icon rows */
.icon-row i{ color: var(--brand-primary); width: 22px; text-align: center; }
.emoji-icon{ display:inline-block; line-height:1; }

/* Count bars */
.count-bar{ display:flex; flex-wrap:wrap; }
.count-pill{
  flex:1 1 150px; min-width: 140px; background:#f8f9fa; border-radius:.5rem; padding:.8rem 1rem; color:#fff; margin:.25rem; text-align:center; position:relative;
  overflow:hidden; box-shadow:0 1px 6px rgba(0,0,0,.06);
}
.count-pill .num{font-weight:700;font-size:1.1rem;}
.count-pill .lbl{font-size:.85rem;opacity:.9;}
.count-total{background:var(--brand-blue);}
.count-sent{background:var(--brand-amber); color:#222;}
.count-pending{background:var(--brand-purple);}

/* RSVP status segmented bar */
.rsvp-bar{display:flex;flex-wrap:wrap;}
.rsvp-seg{
  flex:1 1 180px; min-width:160px; border-radius:.5rem; color:#fff;
  padding:.7rem 1rem; margin:.25rem; text-align:center; box-shadow:0 1px 6px rgba(0,0,0,.06);
}
.rsvp-yes{background:var(--brand-green);}
.rsvp-maybe{background:var(--brand-orange);}
.rsvp-no{background:var(--brand-red);}
.rsvp-nr{background:var(--brand-primary);}
.rsvp-seg .num{font-weight:700;margin-right:.35rem;}

/* Attendance cards */
.att-card{ border-radius:.5rem; box-shadow:0 1px 10px rgba(0,0,0,.07); overflow:hidden; }
.att-head{ background:#f3f3f3; color:#ff7a00; font-weight:700; font-size:.95rem; padding:.6rem .9rem; position:relative; }
.att-body{padding:.9rem;}
.att-box{ background:#f8f9fa; border-radius:.35rem; padding:.6rem .9rem; text-align:center; margin-bottom:.5rem; }
.att-box .h3{ font-weight:700; margin:0; }
.att-head.confirmed{color:#ff7a00;}
.att-head.maybe{color:#ff7a00;}

/* Detail utility cards */
.detail-action-card{
  border:1px solid #e9edf4;
  border-radius:.85rem;
  background:#fff;
  padding:1rem;
  box-shadow:0 4px 14px rgba(15,23,42,.05);
}
.detail-action-card__copy{margin-bottom:.85rem;}
.detail-action-card__title{
  font-size:1.25rem;
  font-weight:700;
  color:#1f2937;
  line-height:1.2;
  margin-bottom:.35rem;
}
.detail-action-card__text{
  color:#4b5563;
  font-size:.95rem;
  line-height:1.35;
}
.detail-action-card__button{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width:10rem;
  padding:.7rem 1.15rem;
  border-radius:.6rem;
  background:#f3f7fd;
  color:#111827;
  font-weight:700;
  text-decoration:none;
}
.detail-action-card__button:hover{
  background:#e9f1fb;
  color:#111827;
  text-decoration:none;
}
.detail-inline-link{
  width:100%;
  border:1px solid #e9edf4;
  border-radius:.85rem;
  background:#fff;
  padding:1rem;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:1rem;
  color:#111827;
  text-align:left;
  box-shadow:0 4px 14px rgba(15,23,42,.05);
}
.detail-inline-link__copy{
  display:flex;
  flex-direction:column;
  min-width:0;
}
.detail-inline-link__title{
  font-size:1.05rem;
  font-weight:700;
  line-height:1.2;
  margin-bottom:.25rem;
}
.detail-inline-link__text{
  color:#4b5563;
  font-size:.95rem;
  line-height:1.35;
}
.detail-inline-link i{
  color:#2563eb;
  font-size:1rem;
  flex-shrink:0;
}

/* Vendors pills */
.vendor-pill{
  background:#eef5ff;border:1px solid #cfe2ff;color:#1b5ad7;
  padding:.35rem .65rem;border-radius:.35rem;margin-right:.35rem;
  font-size:.85rem; font-weight:600;
}

/* Small helpers */
.text-small{font-size:.9rem;}
.link-arrow{color:var(--brand-primary);font-weight:600;}
.link-arrow i{font-size:.9rem;}

/* Badge tabs */
.badge-tabs{display:flex;flex-wrap:wrap;gap:.5rem;}
.badge-tab{
  border:1px solid #dbe2ef;
  background:#f8fafc;
  border-radius:999px;
  padding:.35rem .85rem;
  font-weight:600;
  color:#0f172a;
  transition:all .15s ease;
}
.badge-tab:hover{background:#eef4ff;}
.badge-tab.active{
  background:var(--brand-primary);
  color:#fff;
  border-color:var(--brand-primary);
  box-shadow:0 6px 16px rgba(0,105,217,.25);
}
.badge-tab:focus-visible{outline:2px solid #0d6efd;outline-offset:2px;}

/* Action menu */
.action-menu-toggle{
  border-radius: 10px;
  background: #f2f5fb;
  color: #0f172a;
  border: 1px solid #e3e8f0;
  transition: all .15s ease;
}
.action-menu-toggle:hover{ background:#e7eef9; color:#0b1324; }
.action-menu-toggle:focus-visible{ outline:2px solid #0d6efd; outline-offset:2px; }
.action-menu{
  border-radius: 12px;
  padding: .4rem;
  min-width: 12rem;
  border: 1px solid #e8ecf4;
  box-shadow: 0 12px 30px rgba(15,23,42,.14);
}
.action-menu__item{
  border-radius: 8px;
  padding: .6rem .75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: .65rem;
  color: #111827;
}
.action-menu__item i{ color:#64748b; width:1.1rem; text-align:center; }
.action-menu__item:hover,
.action-menu__item:focus{
  background:#f3f6fb;
  color:#0f172a;
}
@media (max-width: 575.98px){ .page-title{font-size:1rem;} }

/* Skeleton loader */
.skeleton-card{ border-radius:.5rem; box-shadow:0 1px 10px rgba(0,0,0,.07); }
.skeleton-line,
.skeleton-block{
  position:relative; overflow:hidden; background:#e9ecef;
  border-radius:.35rem;
}
.skeleton-line{ height:14px; }
.skeleton-line--lg{ height:22px; width:70%; }
.skeleton-line--md{ width:55%; }
.skeleton-line--sm{ width:40%; }
.skeleton-block{ height:140px; border-radius:.5rem; }
.skeleton-line::after,
.skeleton-block::after{
  content:""; position:absolute; inset:0;
  background:linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
  transform:translateX(-100%);
  animation:skeleton-glow 1.5s ease-in-out infinite;
}
@keyframes skeleton-glow{
  0%{ transform:translateX(-100%); }
  100%{ transform:translateX(100%); }
}

.map-card{
  border:1px solid #e5e7eb;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 8px 20px rgba(15,23,42,.08);
  background:#fff;
}
.map-card__header{
  padding:.75rem 1rem;
  font-weight:600;
  color:#0f172a;
  border-bottom:1px solid #f3f4f6;
  font-size:.95rem;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.5rem;
}
.map-card__header a{
  font-size:.85rem;
  color:var(--brand-primary);
  font-weight:500;
}
.map-frame{
  position:relative;
  padding-top:56.25%;
  background:#f1f5f9;
}
.map-frame__iframe{
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
  border:0;
}
</style>
